import { Metadata } from "@metaplex-foundation/mpl-token-metadata";
import { BN, web3 } from "@project-serum/anchor";
import { Token, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import { TREASURY_INDEX } from "../../common/constants/common.constants";
import {
  AccountMeta,
  LAMPORTS_PER_SOL,
  PublicKey,
  Transaction,
} from "@solana/web3.js";
import { ClubType } from "../../common/enums/club.enum";

import { IClubTreasury } from "../../common/interfaces/clubs.interface";
import { IDetailNftData } from "../../common/interfaces/common.interface";
import { getTreasuryCount } from "../../utilities/helpers";
import {
  depositRecordSeed,
  financialRecordSeed,
  fundraiseCfgSeed,
  governanceSeed,
  monkeAdminSeed,
  programFactory,
  RPC_CONNECTION,
  splGovernanceProgram,
  treasuryDataSeed,
  treasurySeed,
  unqClubMemberSeed,
  unqClubSeed,
  voterWeightSeed,
} from "../common/utilities";
import { sendTransaction } from "../transactions/sendTransaction";
import { ASSOCIATED_PROGRAM_ID } from "@project-serum/anchor/dist/cjs/utils/token";
import { getRemainingAccountForMemberOfNFTClub } from "../common/helpers/nftClubs.helpers";

/**
 * Get mint metadata
 * @param mintMetadataAccount {PublicKey} Club Address
 * @return {Metadata}
 */
export async function getMintMetadata(
  mintMetadataAccount: PublicKey
): Promise<any> {
  try {
    const tokenMetaPubkey = await Metadata.getPDA(mintMetadataAccount);
    const metaData = await Metadata.load(RPC_CONNECTION, tokenMetaPubkey);
    const dataMetadata = await fetch(metaData!.data.data.uri);

    return await dataMetadata.json();
  } catch (err) {
    console.log(err);
    throw err;
  }
}

/**
 * Deposit tokens to club's treasury
 * @param member {AnchorWallet} Member's pubkey
 * @param treasury {IClubTreasury} Club treasury
 * @param realmName {string} Club name
 * @param owner {PublicKey} Owner's pubkey
 * @param depositAmount {number} Deposit amount
 * @return {void}
 */
export async function supportClub(
  member: AnchorWallet,
  treasury: IClubTreasury,
  realmName: string,
  depositAmount: BN,
  supportType: number,
  denominatedTA?: PublicKey,
  membersTA?: PublicKey
) {
  const clubProgram = programFactory(member);
  let remainingAccounts: AccountMeta[] = [];
  let countBuff = Buffer.alloc(4);
  countBuff.writeInt32LE(treasury.fundraiseCount, 0);

  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [treasuryPda] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );
  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasuryPda.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      member.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );
  const [voterWeightAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      voterWeightSeed,
      member.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [fundraise] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasuryData.toBuffer(), fundraiseCfgSeed, countBuff],
    clubProgram.programId
  );

  const [fundraiseDepositRecord] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      treasuryPda.toBuffer(),
      depositRecordSeed,
      member.publicKey.toBuffer(),
      countBuff,
    ],
    clubProgram.programId
  );

  const [financialRecord] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      treasuryData.toBuffer(),
      financialRecordSeed,
      member.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  if (denominatedTA && membersTA) {
    remainingAccounts.push({
      pubkey: denominatedTA,
      isSigner: false,
      isWritable: true,
    });
    remainingAccounts.push({
      pubkey: membersTA,
      isSigner: false,
      isWritable: true,
    });
  }

  const [monkeAdmin] = await PublicKey.findProgramAddress(
    [unqClubSeed, monkeAdminSeed],
    clubProgram.programId
  );

  const monkeAdminAccount = await clubProgram.account.monkeAdmins.fetch(
    monkeAdmin
  );

  try {
    const recentBlockhash = await RPC_CONNECTION.getLatestBlockhash();
    const supportClubTransaction = new Transaction({
      recentBlockhash: recentBlockhash.blockhash,
      feePayer: member.publicKey,
    });

    const supportClubInstruction = clubProgram.instruction.supportClub(
      depositAmount,
      supportType,
      {
        accounts: {
          realm: realmAddress,
          memberData: memberPDA,
          fundraiseConfig: fundraise,
          financialRecord,
          treasuryData: treasuryData,
          clubData,
          feeWallet: monkeAdminAccount.feeWallet,
          monkeAdmins: monkeAdmin,
          treasury: treasuryPda,
          payer: member.publicKey,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: web3.SystemProgram.programId,
        },
        remainingAccounts,
      }
    );
    supportClubTransaction.add(supportClubInstruction);
    const signedTx = await member.signTransaction(supportClubTransaction);
    await sendTransaction({
      transaction: signedTx,
      connection: RPC_CONNECTION,
      wallet: member,
    });
    return {
      voterWeightAddress,
      fundraiseDepositRecord,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Get max sol amount from member's wallet
 * @param member {PublicKey} Member's public key
 * @return {number | undefined} Sol amount from member's account
 */
export async function getMaxSolDepositAmountForTokenOwner(
  member: PublicKey
): Promise<number | undefined> {
  try {
    const parsedAccountInfo: any = await RPC_CONNECTION.getParsedAccountInfo(
      member
    );
    if (parsedAccountInfo.value === null) {
      return 0;
    }
    return parsedAccountInfo.value.lamports / LAMPORTS_PER_SOL;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function getMaxDenomCureencyDepositAmountForTokenOwner(
  mint: PublicKey,
  decimals: number,
  member: PublicKey
): Promise<
  | {
      maxAmount: number;
      membersTA: PublicKey | undefined;
    }
  | undefined
> {
  try {
    const parsedAccountInfo =
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(member, {
        mint: mint,
      });

    if (parsedAccountInfo.value.length === 0) {
      return {
        maxAmount: 0,
        membersTA: undefined,
      };
    }
    const associatedTokenAccountByMint = await Token.getAssociatedTokenAddress(
      ASSOCIATED_PROGRAM_ID,
      TOKEN_PROGRAM_ID,
      mint,
      member
    );

    const membersTa =
      parsedAccountInfo.value.find(
        (item) =>
          item.pubkey.toString() === associatedTokenAccountByMint.toString()
      ) ?? parsedAccountInfo.value[0];

    return {
      maxAmount: membersTa.account.data.parsed.info.tokenAmount.uiAmount,
      membersTA: membersTa.pubkey,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Parse metadata to NFT
 * @param nftMetadata {PublicKey} NFT metadata
 * @param mint {PublicKey} Mint
 * @param tokenAddress? {PublicKey} Token address
 * @param owner? {PublicKey} Owner
 * @param relatedProposal? {PublicKey} Related proposal
 * @return {IDetailNftData} NFT
 */
export function parseMetadataToNft(
  nftMetadata: any,
  mint: PublicKey,
  tokenAddress?: PublicKey,
  owner?: PublicKey,
  relatedProposal?: PublicKey
): IDetailNftData {
  return {
    nftUrl: nftMetadata.image,
    name: nftMetadata.name,
    collection: nftMetadata.collection,
    description: nftMetadata.description,
    tokenAccount: tokenAddress,
    mint: mint,
    owner: owner,
    relatedProposal: relatedProposal,
  };
}
